import { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Switch,
  DatePicker,
  InputNumber,
  Row,
  Col,
  message,
  Tag,
  Upload,
  Button,
} from "antd";
import { BookOutlined, UploadOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "store/slices/modalsSlice";
import { createAssignment } from "store/slices/assignmentsSlice";
import RichTextEditor from "components/shared-components/RichTextEditor";
import TrainingsService from "services/TrainingsService";

const { Option } = Select;

const ATTACHMENT_ACCEPTED_EXTENSIONS =
  ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.webp";

const ATTACHMENT_ACCEPTED_MIME = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
];

const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;

const AddGroupExerciseModal = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const { isModalOpen, modalType, modalProps } = useSelector(
    (state) => state?.modals
  );
  const { actionLoading } = useSelector((state) => state.assignments);
  const { group } = useSelector((state) => state?.groups);

  const open = isModalOpen && modalType === "add_group_exercise";
  const groupId = modalProps?.groupId;
  const formationId = modalProps?.formationId || group?.formationId?._id;
  const groupName = modalProps?.groupName || group?.groupName;

  const [instructors, setInstructors] = useState([]);
  const [instructorsLoading, setInstructorsLoading] = useState(false);
  const [attachmentFileList, setAttachmentFileList] = useState([]);

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({
        type: "exercice",
        submissionRequired: true,
        isPublished: true,
        order: 0,
      });
      setAttachmentFileList([]);
    }
  }, [open, form]);

  const handleBeforeUploadAttachment = (file) => {
    if (!ATTACHMENT_ACCEPTED_MIME.includes(file.type)) {
      message.error(
        "Type de fichier non supporté. Formats acceptés : PDF, Word, Excel, PowerPoint, image."
      );
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      message.error("Le fichier dépasse la taille maximale autorisée (10 Mo).");
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  useEffect(() => {
    if (!open || !formationId) {
      setInstructors([]);
      return;
    }
    setInstructorsLoading(true);
    TrainingsService.getTraining(formationId)
      .then((res) => {
        const training = res?.data?.formation || {};
        const approved = (training.instructors || [])
          .filter((i) => i.isApproved === "approved")
          .map((i) => i.instructorId)
          .filter(Boolean);
        setInstructors(approved);
      })
      .catch(() => setInstructors([]))
      .finally(() => setInstructorsLoading(false));
  }, [open, formationId]);

  const handleCancel = () => {
    dispatch(closeModal());
  };

  const handleSubmit = async (values) => {
    if (!formationId || !groupId) {
      message.error("Formation ou groupe introuvable");
      return;
    }

    const formData = new FormData();
    formData.append("formationId", formationId);
    formData.append("title", values.title || "");
    formData.append("scope", "formation");
    formData.append("type", values.type || "exercice");
    formData.append("submissionRequired", values.submissionRequired !== false);
    formData.append("isPublished", values.isPublished === true);
    formData.append("solutionVisible", false);
    formData.append("order", values.order || 0);
    formData.append("groupIds", JSON.stringify([groupId]));
    formData.append("instructorIds", JSON.stringify(values.instructorIds || []));

    if (values.dueDate) {
      formData.append("dueDate", values.dueDate.toISOString());
    }
    if (values.instructionsText !== undefined) {
      formData.append("instructionsText", values.instructionsText);
    }
    if (attachmentFileList.length > 0 && attachmentFileList[0].originFileObj) {
      formData.append("instructionsFile", attachmentFileList[0].originFileObj);
    }

    try {
      await dispatch(createAssignment(formData)).unwrap();
      message.success("Exercice créé avec succès pour ce groupe");
      dispatch(closeModal());
    } catch (err) {
      message.error(err?.message || "Erreur lors de la création de l'exercice");
    }
  };

  return (
    <Modal
      title={
        <>
          <BookOutlined style={{ color: "#1890ff", marginRight: 8 }} />
          Ajouter un exercice pour {groupName ? <Tag color="blue">{groupName}</Tag> : "ce groupe"}
        </>
      }
      open={open}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      confirmLoading={actionLoading}
      width={700}
      okText="Créer"
      cancelText="Annuler"
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="type"
              label="Type de devoir"
              rules={[{ required: true }]}
            >
              <Select>
                <Option value="exercice">Exercice pratique</Option>
                <Option value="projet_final">Projet final</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="dueDate" label="Date d'échéance (optionnelle)">
              <DatePicker
                showTime
                format="DD/MM/YYYY HH:mm"
                style={{ width: "100%" }}
                placeholder="Date & Heure limite"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="title"
          label="Titre de l'exercice / projet"
          rules={[{ required: true, message: "Le titre est obligatoire" }]}
        >
          <Input placeholder="Ex: TP 1 - Création d'une base de données relationnelle" />
        </Form.Item>

        <Form.Item
          name="instructorIds"
          label="Formateur(s) responsable(s)"
          rules={[
            {
              required: true,
              message: "Veuillez sélectionner au moins un formateur",
            },
          ]}
        >
          <Select
            mode="multiple"
            loading={instructorsLoading}
            placeholder="Choisir le(s) formateur(s) responsable(s)..."
            optionFilterProp="children"
            allowClear
          >
            {instructors.map((i) => (
              <Option key={i._id} value={i._id}>
                {i.fullName}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="instructionsText" label="Instructions écrites (Texte riche)">
          <RichTextEditor placeholder="Rédigez l'énoncé de l'exercice, les consignes et les barèmes..." />
        </Form.Item>

        <Form.Item label="Pièce jointe (PDF, Word, Excel, PowerPoint, image)">
          <Upload
            beforeUpload={handleBeforeUploadAttachment}
            fileList={attachmentFileList}
            onChange={({ fileList }) => setAttachmentFileList(fileList.slice(-1))}
            onRemove={() => setAttachmentFileList([])}
            accept={ATTACHMENT_ACCEPTED_EXTENSIONS}
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>Joindre un fichier</Button>
          </Upload>
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="order" label="Ordre d'affichage">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="submissionRequired"
              label="Rendu obligatoire ?"
              valuePropName="checked"
            >
              <Switch checkedChildren="Oui" unCheckedChildren="Non" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="isPublished"
              label="Publier immédiatement ?"
              valuePropName="checked"
            >
              <Switch checkedChildren="Publié" unCheckedChildren="Brouillon" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default AddGroupExerciseModal;
